// Teachers.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db, firebaseConfig   } from '../config/fbConfig';
import { collection, setDoc, doc, getDocs, updateDoc,  Timestamp } from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { initializeApp, getApp, getApps } from 'firebase/app';
import * as XLSX from 'xlsx'; // For Excel file parsing
import Papa from 'papaparse'; // For CSV file parsing

let secondaryApp;
let secondaryAuth;

if (!getApps().some(app => app.name === 'Secondary')) {
  secondaryApp = initializeApp(firebaseConfig, 'Secondary');
} else {
  secondaryApp = getApp('Secondary');
}

secondaryAuth = getAuth(secondaryApp);

Modal.setAppElement('#root');

const Teachers = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    qualification: '',
    field: '',
    phone: '',
    email: '',
    isActive: true,
    status: 'Teacher'
  });
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingTeacher, setViewingTeacher] = useState(null);
  const [isFetchingStaff, setIsFetchingStaff] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel'); // 'excel' or 'csv'
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');

  const usersCollectionRef = collection(db, "users");

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async (searchTerm = "") => {
    setIsFetchingStaff(true);
    try {
      const normalizedSearchTerm = searchTerm.trim().toLowerCase();
      
      if (normalizedSearchTerm === "all") {
        // Fetch ALL staff
        const data = await getDocs(usersCollectionRef);
        const allStaff = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        filterAndSetStaff(allStaff);
      } 
      else if (searchTerm.trim()) {
        // Fetch all and filter locally for case-insensitive partial matching
        const data = await getDocs(usersCollectionRef);
        const allStaff = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        
        const filteredStaff = allStaff.filter(staff => {          
          const fullName = `${staff.firstName || ''} ${staff.lastName || ''}`.toLowerCase();
          const phone = staff.phone ? staff.phone.toString().toLowerCase() : '';
          const field = (staff.field || '').toLowerCase();
          const status = (staff.status || '').toLowerCase();
          
          return (
            fullName.includes(normalizedSearchTerm) ||
            phone.includes(normalizedSearchTerm) ||
            field.includes(normalizedSearchTerm) ||
            status.includes(normalizedSearchTerm)
          );
        });
        
        filterAndSetStaff(filteredStaff);
      } 
      else {
        // Default: show nothing when search is empty
        setTeachers([]);
        setCashiers([]);
        setAdmins([]);
      }
    } catch (error) {
      setError("Failed to fetch staff");
      setShowModal(true);
    } finally {
      setIsFetchingStaff(false);
    }
  };

  // Helper function to categorize staff
  const filterAndSetStaff = (staffList) => {
    setTeachers(staffList.filter(t => t.status === 'Teacher'));
    setCashiers(staffList.filter(t => t.status === 'Cashier'));
    setAdmins(staffList.filter(t => t.status === 'Admin' && t.level === undefined));
  };

  // Update the search input to trigger search on Enter or button click
  const handleSearch = (e) => {
    e.preventDefault();
    fetchStaff(searchQuery);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

const handleAddTeacher = async () => {
    setError("");
    setSuccessMessage("");
    const { firstName, lastName, qualification, field, phone, email } = formData;
  
    if (!firstName || !lastName || !qualification || !field || !phone || !email) {
      setError("All fields are required");
      setShowModal(true);
      return;
    }
  
    setUpdate(true);
    try {
      // Create user using secondary auth instance
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, '123456');
      const uid = userCredential.user.uid;
  
      const userDocRef = doc(db, "users", uid);
      await setDoc(userDocRef, {
        uid,
        firstName,
        lastName,
        qualification,
        field,
        phone,
        email,
        status: "Teacher",
        isActive: true,
        teachingAssignments: [],
        createdAt: Timestamp.now(),
      });
  
      setSuccessMessage("Teacher added successfully");
  
      // Reset form and refresh
      fetchStaff();
      setFormData({
        firstName: '',
        lastName: '',
        qualification: '',
        field: '',
        phone: '',
        email: '',
        isActive: true,
        status: 'Teacher'
      });
      setIsAddModalOpen(false);
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setError("Email already in use. Please use a different email.");
      } else {
        setError("Failed to add teacher: " + error.message);
      }
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };
  
  const handleUpdateTeacher = async () => {
    setError("");
    setSuccessMessage("");
    setUpdate(true);
    try {
      const teacherDoc = doc(db, "users", editingTeacher.id);
      await updateDoc(teacherDoc, {
        ...formData,
        // Ensure isActive is properly included
        isActive: formData.isActive !== undefined ? formData.isActive : true
      });
      setSuccessMessage("Teacher updated successfully");
      fetchStaff();
      setEditingTeacher(null);
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update teacher: " + error.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditTeacher = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({ ...teacher });
    setIsEditModalOpen(true);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };
  
  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
  };
  
  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setImportStatus('Processing file...');
    setImportProgress(0);
  
    try {
      let teachersData = [];
  
      if (fileType === 'excel') {
        // Process Excel file
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        teachersData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        // Process CSV file
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        teachersData = result.data;
      }
  
      if (teachersData.length === 0) {
        throw new Error("No teacher data found in the file");
      }
  
      setImportStatus(`Importing ${teachersData.length} teachers...`);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < teachersData.length; i++) {
        try {
          const teacher = teachersData[i];
          
          // Validate required fields
          if (!teacher.firstName || !teacher.lastName || !teacher.email) {
            errorCount++;
            continue;
          }
  
          // Create auth account first
          const userCredential = await createUserWithEmailAndPassword(
            secondaryAuth, 
            teacher.email, 
            '123456' // Default password
          );
          const uid = userCredential.user.uid;
  
          // Create Firestore document
          const teacherData = {
            uid,
            firstName: teacher.firstName || '',
            lastName: teacher.lastName || '',
            qualification: teacher.qualification || '',
            field: teacher.field || '',
            phone: teacher.phone || '',
            email: teacher.email || '',
            status: teacher.status || 'Teacher',
            isActive: teacher.isActive !== undefined ? teacher.isActive : true,
            teachingAssignments: [],
            createdAt: Timestamp.now()
          };
  
          await setDoc(doc(db, "users", uid), teacherData);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing teacher ${i + 1}:`, error);
        }
  
        // Update progress
        setImportProgress(Math.round(((i + 1) / teachersData.length) * 100));
      }
  
      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Bulk import completed: ${successCount} teachers added successfully`);
      fetchStaff();
    } catch (error) {
      setError(`Bulk import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsBulkImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2000);
    }
  };

  if (loading || !userData) return <Loader text={loading ? "LOADING" : "READING"} />;

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text="PROCESSING" />}
      {allowedRoles.includes(userData.status) ? (
        <>
      <div className="top-elements row align-center space-between">
        <Link to="/home" className="back-link">
          <i className="fa-solid fa-arrow-left size3"></i>
        </Link>
        <div className="column align-center justify-center">
          <img src={Logo} className="middle-logo" alt="logo" />
        </div>
        <Link to="#" className="home-link" onClick={() => {fetchStaff()}}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>
      <div className="login-page-items column align-center scroll-fixed">
        <h2 className="manage-page-heading caps size4">Manage Teachers</h2>        
        <div className="row justify-center align-center gap-15 add-buttons">
          <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>
            Add Teacher
          </button>
          <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>
            Bulk Import
          </button>
        </div>
        <div className="search-container">
          <form onSubmit={handleSearch} className="row space-between">
            <input
              type="text"
              placeholder="Search by name, phone or field ('All' for all staff)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(e);
                }
              }}
            />
            <button type="submit" className="login-button login-button-search">
              <i className="fas fa-search"></i>
            </button>
          </form>
        </div>

        <div className="scroll-fixed scroll-fixed2">
          <div className="class-list class-list-d scrollable">
          {isFetchingStaff ? (
              <p className="male-female-stat">Loading teachers...</p>
            ) : teachers.length === 0 ? (
              <p className="male-female-stat">Search to view teachers of enter 'All' to view all teachers</p>
            ) : (
              teachers.map((teacher) => (
              <div key={teacher.id} className="class-item">                
                <p>{teacher.firstName} {teacher.lastName} {!teacher.isActive && <span className="size5 font-red">(Inactive)</span>} <br />({teacher.phone})<br /><span className="size5">{teacher.field}</span></p>
                <div className="row align-center justify-center gap-15">
                    <button className="list-btn" onClick={() => { setViewingTeacher(teacher); setIsViewModalOpen(true); }}>
                        <i className="fas fa-eye"></i>
                    </button>
                    <button className="list-btn" onClick={() => handleEditTeacher(teacher)}><i className="fas fa-edit"></i></button>
                </div>
              </div>
            ))
          )}
          {isFetchingStaff ? (
              <p></p>
            ) : cashiers.length === 0 ? (
              <p></p>
            ) : (
              <>
                <p className="size5 bold">CASHIERS</p>
                {cashiers.map((teacher) => (
                  <div key={teacher.id} className="class-item">
                    <p>
                      {teacher.firstName} {teacher.lastName} <br />({teacher.phone})<br />
                      <span className="size5">{teacher.status}</span>
                    </p>
                    <div className="row align-center justify-center gap-15">
                      <button
                        className="list-btn"
                        onClick={() => {
                          setViewingTeacher(teacher);
                          setIsViewModalOpen(true);
                        }}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        className="list-btn"
                        onClick={() => handleEditTeacher(teacher)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          {isFetchingStaff ? (
              <p></p>
            ) : admins.length === 0 ? (
              <p></p>
            ) : (
              <>
                <p className="size5 bold">ADMINS</p>
                {admins.map((teacher) => (
                  <div key={teacher.id} className="class-item">
                    <p>
                      {teacher.firstName} {teacher.lastName} <br />({teacher.phone})<br />
                      <span className="size5">{teacher.status}</span>
                    </p>
                    <div className="row align-center justify-center gap-15">
                      <button
                        className="list-btn"
                        onClick={() => {
                          setViewingTeacher(teacher);
                          setIsViewModalOpen(true);
                        }}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        className="list-btn"
                        onClick={() => handleEditTeacher(teacher)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
      </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal isOpen={isAddModalOpen} onRequestClose={() => setIsAddModalOpen(false)} contentLabel="Add Teacher" className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Teacher</h2>
        <form className="session-form form-scroll">
          <input name="firstName" className="input-field" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} />
          <input name="lastName" className="input-field" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} />
          <input name="qualification" className="input-field" placeholder="Qualification" value={formData.qualification} onChange={handleInputChange} />
          <input name="field" className="input-field" placeholder="Field of Study" value={formData.field} onChange={handleInputChange} />
          <input name="phone" className="input-field" placeholder="Phone Number" value={formData.phone} onChange={handleInputChange} />
          <input name="email" className="input-field" placeholder="Email" value={formData.email} onChange={handleInputChange} />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddTeacher}>Add Teacher</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isEditModalOpen} onRequestClose={() => {
          setIsEditModalOpen(false);
          setFormData({
            firstName: '',
            lastName: '',
            qualification: '',
            field: '',
            phone: '',
            email: '',
            isActive: true,
            status: 'Teacher'
          }); // Reset formData
          setEditingTeacher(null); // Reset editingTeacher
        }} contentLabel="Edit Teacher" className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Teacher</h2>
        <form className="session-form form-scroll">
          <input name="firstName" className="input-field" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} />
          <input name="lastName" className="input-field" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} />
          <input name="qualification" className="input-field" placeholder="Qualification" value={formData.qualification} onChange={handleInputChange} />
          <input name="field" className="input-field" placeholder="Field of Study" value={formData.field} onChange={handleInputChange} />
          <input name="phone" className="input-field" placeholder="Phone Number" value={formData.phone} onChange={handleInputChange} />
          <select
            name="status"
            className="input-field"
            value={formData.status}
            onChange={handleInputChange}
          >
            <option value="">Select status</option>
            <option value="Parent">Parent</option>
            <option value="Cashier">Cashier</option>
            <option value="Teacher">Teacher</option>
            <option value="Admin">Admin</option>
          </select>
          <div className="row align-center gap-1">
            <label>
              <input
                type="checkbox"
                name="isActive"
                className="gold-tick male-female-stat"
                checked={formData.isActive}
                onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
              />
              Active
            </label>
          </div>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setFormData({
                  firstName: '',
                  lastName: '',
                  qualification: '',
                  field: '',
                  phone: '',
                  email: '',
                  isActive: true,
                  status: 'Teacher'
                }); // Reset formData
                setEditingTeacher(null); // Reset editingTeacher
              }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateTeacher}>Update Teacher</button>
          </div>
        </form>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
          setImportProgress(0);
          setImportStatus('');
        }}
        contentLabel="Bulk Import Teachers"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Teachers</h2>
        <div className="modal-form">
          <div style={{marginBottom: '5px'}}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          {importStatus && (
            <div>
              <p>{importStatus}</p>
              {importProgress > 0 && (
                <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}>
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '20px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '5px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      color: 'white'
                    }}
                  >
                    {importProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="row align-center justify-center gap-15">
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
                setImportProgress(0);
                setImportStatus('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={processBulkImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED FIELDS:</strong> All fields are required</p>
            <p><strong>DEFAULT PASSWORD:</strong> 123456 (teachers should change this after first login)</p>
            <p><strong>FOR EXCEL:</strong> First row should contain column headers</p>
            <p><strong>FOR CSV:</strong> First row should contain column headers, comma-separated</p>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isViewModalOpen} onRequestClose={() => setIsViewModalOpen(false)} contentLabel="View Teacher Details" className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Teacher Details</h2>
        {viewingTeacher ? (
            <div className="column gap-1">
            <p><strong>First Name:</strong> {viewingTeacher.firstName}</p>
            <p><strong>Last Name:</strong> {viewingTeacher.lastName}</p>
            <p><strong>Qualification:</strong> {viewingTeacher.qualification}</p>
            <p><strong>Field of Study:</strong> {viewingTeacher.field}</p>
            <p><strong>Phone:</strong> {viewingTeacher.phone}</p>
            <p><strong>Email:</strong> {viewingTeacher.email}</p>
            <p><strong>Status:</strong> {viewingTeacher.status}</p>
            <p className="font-red"><strong>Active:</strong> {viewingTeacher.isActive ? 'Yes' : 'No'}</p>
            <button type="button" className="login-button caps" onClick={() => setIsViewModalOpen(false)}>
              Close
            </button>
            </div>
        ) : (
            <p>No teacher selected</p>
        )}
    </Modal>    
    </>
  );
};

export default Teachers;